/**
 * Chat con el Asistente IA dentro del expediente (Sprint 17.3).
 *
 * Pinta un panel embebido en `<ExpedienteDetail>`:
 *
 * - **Header** con título + cuota restante del mes.
 * - **Lista de turns** (user/assistant) con bubbles, auto-scroll.
 * - **Sugerencias** clickeables siempre visibles (4 prompts curados
 *   para el contexto del expediente — distintos a los del admin chat).
 * - **Composer** con textarea + Enter envía.
 * - **Rating 👍/👎** sobre cada respuesta del assistant (persistido vía
 *   `/assistant/{audit_id}/rate`).
 *
 * **Gating UX:**
 *
 * - Si `disabled` (tier Esencial o cuota agotada), el panel muestra
 *   un estado bloqueado con CTA "Subir a Pro" o "Cuota agotada". No
 *   intenta enviar — el bloqueo está en el FE para no quemar tiempo
 *   de red, pero el BE también lo valida (defensivo).
 * - Si el endpoint devuelve 402, capturamos `detailData.reason` para
 *   discriminar `tier_locked_pro_required` vs `ai_quota_exhausted` y
 *   mostrar mensaje preciso.
 *
 * **Sin streaming:** respuestas completas (~1-3s con Haiku 4.5). V2
 * agrega streaming si el feedback de usuarios lo justifica.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";

import { InlineSpinner } from "@/components/InlineSpinner";
import { MarkdownLite } from "@/components/MarkdownLite";
import { QuotaProgressBar } from "@/components/QuotaProgressBar";
import {
  ApiError,
  fetchAssistantThread,
  postExpedienteAssistantMessage,
  rateExpedienteAssistantResponse,
  type AssistantMessageDTO,
  type AssistantResponseDTO,
  type AssistantToolCallDTO,
  type QuotaUsageDTO,
} from "@/lib/api";
import { track } from "@/lib/analytics";

/**
 * Prompts sugeridos para el contexto de un expediente. Cubren los
 * 4 casos de uso más comunes que el funcionario tiene en el día a
 * día — no es exhaustivo, el campo libre sirve para todo lo demás.
 */
const SUGGESTED_PROMPTS = [
  "¿Qué me falta para avanzar?",
  "¿A qué estado puede pasar este expediente?",
  "¿Qué proyectos similares de mi peer group han sido aprobados?",
  "Dame un ejemplo de subsanación para mi sector.",
];

interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  /** Solo para turns de assistant. */
  auditId?: string;
  toolCalls?: AssistantToolCallDTO[];
  truncated?: boolean;
  /** Estado del rating de este turn (null = sin ratear todavía). */
  rating?: 1 | -1 | null;
}

interface ExpedienteAssistantPanelProps {
  projectId: string;
  municipality: string;
  /**
   * Si el plan del muni no incluye el asistente IA, el panel renderiza
   * el estado bloqueado con CTA a upgrade en vez del chat.
   */
  hasAssistant: boolean;
  /**
   * Cuota mensual de mensajes IA — objeto completo del plan para que
   * el header pueda renderizar una progress bar (no un número crudo,
   * que generaba fricción). Null cuando no hay info (loading / esencial).
   *
   * Sprint 18 hotfix: antes era `quotaRemaining: number | null` y se
   * mostraba "X mensajes restantes este mes". Feedback del founder:
   * el número generaba ansiedad ("solo tengo 150"). Reemplazado por
   * progress bar muda con tooltip para detalles a demanda.
   */
  quota: QuotaUsageDTO | null;
  /**
   * Callback opcional cuando se consume un mensaje exitoso. Le pasa
   * el `quota_remaining` devuelto por el endpoint para que el padre
   * pueda invalidar el query de plan o actualizar un badge.
   */
  onQuotaUpdated?: (newRemaining: number) => void;
}

export function ExpedienteAssistantPanel({
  projectId,
  municipality,
  hasAssistant,
  quota,
  onQuotaUpdated,
}: ExpedienteAssistantPanelProps) {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState("");
  const [gateError, setGateError] = useState<{
    reason: string;
    upgradeTo: string | null;
  } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Sprint Pre-Demo: telemetría de engagement con el panel del asistente.
  // - `mountedAtRef` / `messagesSentRef`: contadores para el evento de
  //   unmount (duration_ms y messages_sent_in_session).
  // - `openedRef` / `threadLoadedRef`: guards para que los eventos
  //   `assistant_panel_opened` y `assistant_thread_loaded` se disparen
  //   UNA sola vez por mount.
  // Arranca en 0 y no en `Date.now()`: leer el reloj durante el render es una
  // función impura. El valor real lo asigna el efecto de mount de abajo, que
  // corre siempre antes de que el cleanup pueda leerlo.
  const mountedAtRef = useRef(0);
  const messagesSentRef = useRef(0);
  const openedRef = useRef(false);
  const threadLoadedRef = useRef(false);

  /**
   * Thread persistido del asistente (Fase 3 demo — memoria por expediente).
   *
   * Hidrata `turns` UNA SOLA VEZ con los últimos N msgs persistidos cuando
   * el panel se monta y aún no hay turns locales. Posteriores envíos del
   * usuario se acumulan en `turns` local; el BE persiste cada turn nuevo
   * en backend (T5 BE) — no reseteamos el state al refetch para no perder
   * mensajes en flight.
   *
   * Si el endpoint falla (403, 5xx, red), fallback silencioso a estado
   * vacío: el panel sigue siendo usable aunque la memoria no esté
   * disponible. `gcTime: 0` + `retry: false` evitan reintentos y caching
   * cruzado entre expedientes que no aportan al funcionario.
   */
  const threadQuery = useQuery({
    queryKey: ["assistant-thread", projectId, municipality],
    queryFn: () => fetchAssistantThread(projectId, municipality),
    enabled: hasAssistant,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Hidratamos durante el render y no en un efecto: con el efecto, el panel
  // pintaba un frame vacío antes de mostrar la conversación persistida.
  // `hydratedFrom` arranca en `undefined` a propósito para que un `data` que
  // ya venga del cache de TanStack en el primer render también hidrate.
  const [hydratedFrom, setHydratedFrom] = useState<typeof threadQuery.data>(undefined);
  if (hydratedFrom !== threadQuery.data) {
    setHydratedFrom(threadQuery.data);
    const data = threadQuery.data;
    // Solo hidratamos si todavía no hay turns locales. Si el user ya mandó
    // un msg (turns.length > 0), no re-hidratamos.
    if (data && data.length > 0 && turns.length === 0) {
      setTurns(
        data.map((m) => ({
          role: m.role,
          content: m.content,
        })),
      );
    }
  }

  /**
   * Sprint Pre-Demo: telemetría del open del panel.
   *
   * Disparamos UNA vez por mount, esperando a que el fetch del thread
   * persistido termine para tener el count real de mensajes previos.
   * En el panel locked (`!hasAssistant`) no entramos a este branch — el
   * componente devuelve `<LockedPanel>` antes y los hooks no corren.
   */
  useEffect(() => {
    if (openedRef.current) return;
    if (threadQuery.isLoading) return;
    openedRef.current = true;
    const count = threadQuery.data?.length ?? 0;
    track({
      event_name: "assistant_panel_opened",
      properties: {
        project_id: projectId,
        thread_messages_count: count,
        was_panel_fresh: count === 0,
      },
    });
  }, [threadQuery.isLoading, threadQuery.data, projectId]);

  /**
   * Sprint Pre-Demo: telemetría del close del panel.
   *
   * Cleanup function del useEffect — se dispara cuando el componente
   * se desmonta (navegación a otra parte, switch de expediente) o
   * cuando cambia `projectId` (se considera "cerrar el panel anterior
   * y abrir uno nuevo"). `messages_sent_in_session` cuenta cuántos
   * mensajes del usuario pasaron por este panel, **independientemente**
   * de si hubo mensajes o no — es métrica de engagement, queremos saber
   * también cuándo abren y se van sin escribir.
   */
  useEffect(() => {
    // Resetear el reloj y el contador cuando empieza la "sesión" de un
    // panel (mount o switch de projectId). También los guards de los
    // eventos one-shot — un switch de expediente abre un panel "nuevo"
    // y queremos que opened/thread_loaded se vuelvan a disparar.
    mountedAtRef.current = Date.now();
    messagesSentRef.current = 0;
    openedRef.current = false;
    threadLoadedRef.current = false;
    const currentProjectId = projectId;
    return () => {
      track({
        event_name: "assistant_panel_closed",
        properties: {
          project_id: currentProjectId,
          duration_ms: Date.now() - mountedAtRef.current,
          messages_sent_in_session: messagesSentRef.current,
        },
      });
    };
  }, [projectId]);

  /**
   * Sprint Pre-Demo: telemetría del thread persistido (memoria).
   *
   * Dispara UNA vez cuando el query del thread termina con éxito.
   * `thread_had_content` permite cruzar contra `was_panel_fresh` del
   * evento opened — si el funcionario abre un panel CON memoria
   * persistida, sabemos que está retomando una conversación.
   */
  useEffect(() => {
    if (threadLoadedRef.current) return;
    if (!threadQuery.isSuccess || !threadQuery.data) return;
    threadLoadedRef.current = true;
    const count = threadQuery.data.length;
    track({
      event_name: "assistant_thread_loaded",
      properties: {
        project_id: projectId,
        messages_loaded: count,
        thread_had_content: count > 0,
      },
    });
  }, [threadQuery.isSuccess, threadQuery.data, projectId]);

  const sendMutation = useMutation<AssistantResponseDTO, Error, AssistantMessageDTO[]>({
    mutationFn: (messages) => postExpedienteAssistantMessage(projectId, municipality, messages),
    onSuccess: (resp) => {
      setTurns((prev) => [
        ...prev,
        {
          role: "assistant",
          content: resp.text,
          auditId: resp.audit_id,
          toolCalls: resp.tool_calls,
          truncated: resp.truncated,
          rating: null,
        },
      ]);
      onQuotaUpdated?.(resp.quota_remaining);
      setGateError(null);

      // Sprint 17.5 telemetría: una vez por respuesta exitosa.
      // Properties curadas — sin texto del mensaje (PII potencial).
      track({
        event_name: "assistant_response_received",
        properties: {
          project_id: projectId,
          dane: municipality,
          audit_id: resp.audit_id,
          tool_calls_count: resp.tool_calls.length,
          tool_calls_has_errors: resp.tool_calls.some((tc) => tc.is_error),
          input_tokens: resp.input_tokens,
          output_tokens: resp.output_tokens,
          latency_ms: resp.latency_ms,
          truncated: resp.truncated,
          quota_remaining: resp.quota_remaining,
        },
      });
    },
    onError: (err) => {
      // Si fue un 402 con detail estructurado, capturamos el motivo
      // para que la UI cambie a estado bloqueado en vez de mostrar
      // un error genérico.
      if (err instanceof ApiError && err.status === 402 && err.detailData !== null) {
        const reason = String(err.detailData.reason ?? "unknown");
        const upgradeTo =
          typeof err.detailData.upgrade_to === "string" ? err.detailData.upgrade_to : null;
        setGateError({ reason, upgradeTo });

        // Sprint 17.5 telemetría: gates desde el endpoint.
        // Distinto evento por razón para que el founder pueda
        // distinguir "no compraron Pro" vs "Pro pero agotada".
        track({
          event_name:
            reason === "ai_quota_exhausted" ? "assistant_quota_blocked" : "assistant_tier_blocked",
          properties: {
            project_id: projectId,
            dane: municipality,
            reason,
            upgrade_to: upgradeTo,
          },
        });
      }
    },
  });

  // Auto-scroll al fondo en cada cambio (nuevo turn o pending).
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, sendMutation.isPending]);

  function handleSend(e: FormEvent) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sendMutation.isPending) return;

    const nextTurns: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(nextTurns);
    setDraft("");

    // Sprint 17.5 telemetría: cada mensaje del usuario. `turn_index`
    // ayuda a distinguir primera pregunta vs. follow-ups (señal de
    // engagement). Length del texto sin el texto en sí — preserva
    // privacidad y mide si los usuarios escriben prompts cortos o
    // detallados.
    track({
      event_name: "assistant_message_sent",
      properties: {
        project_id: projectId,
        dane: municipality,
        turn_index: nextTurns.length - 1,
        content_length: text.length,
      },
    });
    // Sprint Pre-Demo: contador local de mensajes enviados en esta
    // sesión del panel. Usado en `assistant_panel_closed` para medir
    // engagement (abrió y se fue sin escribir vs. conversación larga).
    messagesSentRef.current += 1;

    // El BE solo necesita role+content. Tool calls + audit ID son del FE.
    const payload: AssistantMessageDTO[] = nextTurns.map((t) => ({
      role: t.role,
      content: t.content,
    }));
    sendMutation.mutate(payload);
  }

  // Estado bloqueado: panel locked con CTA. Cubre 3 casos:
  // 1. Plan Esencial (hasAssistant === false desde el padre).
  // 2. Endpoint devolvió 402 tier_locked_pro_required.
  // 3. Endpoint devolvió 402 ai_quota_exhausted.
  if (!hasAssistant || gateError) {
    return (
      <LockedPanel
        reason={
          gateError?.reason ?? (hasAssistant ? "ai_quota_exhausted" : "tier_locked_pro_required")
        }
        upgradeTo={gateError?.upgradeTo ?? null}
      />
    );
  }

  // Mensaje user-facing del error. **Política:** nunca exponer jerga
  // técnica al funcionario municipal. Reglas:
  // - 402 ya se maneja con LockedPanel (no llega acá).
  // - 422/400: validación falló (probablemente bug de FE). Mensaje suave.
  // - 429: rate limit (>5/min). Mensaje accionable.
  // - 503: asistente no disponible (config del backend). Mensaje específico.
  // - 5xx (500/502/504): falla server-side, NO confiamos en el detail del
  //   backend (puede contener stack traces o str(exception) sin curar).
  // - Network / errores no-ApiError: usamos el mensaje (ya curado en
  //   `errors.ts`).
  let errorMessage: string | null = null;
  if (sendMutation.error instanceof ApiError && sendMutation.error.status !== 402) {
    const status = sendMutation.error.status;
    if (status === 503) {
      errorMessage =
        "El asistente no está disponible en este momento. Intenta más tarde " +
        "o escríbenos si persiste.";
    } else if (status === 429) {
      errorMessage = "Demasiados mensajes en poco tiempo. Espera un momento e intenta de nuevo.";
    } else if (status === 422 || status === 400) {
      errorMessage =
        "No pudimos enviar tu mensaje. Intenta reformularlo o, si persiste, " + "escríbenos.";
    } else if (status >= 500) {
      // Defensivo: no mostrar `error.message` que puede traer detail
      // técnico no curado del backend en un 5xx accidental.
      errorMessage = "Hubo un problema consultando al asistente. Intenta de nuevo en un momento.";
    } else {
      // 4xx específicos (404, 403, etc.) — el backend cura su detail.
      errorMessage = sendMutation.error.message;
    }
  } else if (sendMutation.error instanceof Error && !(sendMutation.error instanceof ApiError)) {
    errorMessage = sendMutation.error.message;
  }

  return (
    <section
      aria-label="Asistente IA"
      data-testid="expediente-assistant"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs"
    >
      <header className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">
            Asistente IA
            <span className="ml-2 inline-block rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-brand-700 uppercase">
              Pro
            </span>
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Pregúntale al asistente sobre este expediente — no inventa, solo consulta tus datos.
          </p>
        </div>
        {quota && (
          <div data-testid="assistant-quota" className="flex shrink-0 flex-col items-end gap-1">
            <span className="text-[10px] font-semibold tracking-wide text-slate-500 uppercase">
              Uso este mes
            </span>
            <QuotaProgressBar quota={quota} widthClass="w-32" />
          </div>
        )}
      </header>

      {/* Lista de turns */}
      <div
        ref={scrollRef}
        data-testid="assistant-scroll"
        className="max-h-[420px] min-h-[200px] overflow-y-auto rounded-xl border border-slate-100 bg-slate-50 p-3"
      >
        {threadQuery.isLoading && turns.length === 0 ? (
          <ThreadLoadingState />
        ) : turns.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="space-y-3">
            {turns.map((turn, idx) => (
              <TurnBubble
                key={idx}
                turn={turn}
                projectId={projectId}
                municipality={municipality}
                onRated={(rating) => {
                  setTurns((prev) => prev.map((t, i) => (i === idx ? { ...t, rating } : t)));
                }}
              />
            ))}
            {sendMutation.isPending && <PendingBubble />}
          </ul>
        )}
      </div>

      {/* Errores no-402 (red, 5xx, 429) */}
      {errorMessage && (
        <div
          data-testid="assistant-error"
          className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          {errorMessage}
        </div>
      )}

      {/* Sugerencias */}
      <SuggestionChips
        onPick={(prompt) => {
          setDraft(prompt);
          // Telemetría: cuáles sugerencias usa la gente. El founder
          // puede iterar el set basado en cuáles tienen CTR alto.
          track({
            event_name: "assistant_suggestion_clicked",
            properties: {
              project_id: projectId,
              dane: municipality,
              prompt,
            },
          });
        }}
        disabled={sendMutation.isPending}
      />

      {/* Composer */}
      <form onSubmit={handleSend} className="mt-3 flex gap-2">
        <textarea
          data-testid="assistant-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend(e as unknown as FormEvent);
            }
          }}
          disabled={sendMutation.isPending}
          placeholder="Pregunta algo sobre este expediente..."
          rows={2}
          className="flex-1 resize-none rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-hidden disabled:bg-slate-50"
        />
        <button
          type="submit"
          data-testid="assistant-send"
          disabled={sendMutation.isPending || !draft.trim()}
          className="inline-flex cursor-pointer items-center gap-2 self-end rounded-md bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-xs transition-colors hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {sendMutation.isPending ? (
            <>
              <InlineSpinner tone="brand" />
              Pensando…
            </>
          ) : (
            "Enviar"
          )}
        </button>
      </form>
    </section>
  );
}

// ----- Sub-components -------------------------------------------------------

function EmptyState() {
  return (
    <div data-testid="assistant-empty" className="py-8 text-center text-slate-500">
      <p className="text-sm font-semibold text-slate-700">Empieza con una pregunta</p>
      <p className="mt-1 text-xs">
        Usa una sugerencia o escribe directamente abajo. El asistente solo consulta datos del
        expediente — no inventa ni redacta textos formales.
      </p>
    </div>
  );
}

/**
 * Loader breve mientras se carga el thread persistido al abrir el panel.
 *
 * Fase 3 demo: el endpoint suele responder en <100ms (índice por
 * `project_id` + LIMIT 10), así que el flash es mínimo. Si el fetch
 * falla, caemos a `EmptyState` (fallback silencioso — el panel sigue
 * siendo usable aunque la memoria no esté disponible).
 */
function ThreadLoadingState() {
  return (
    <div
      data-testid="assistant-thread-loading"
      className="flex items-center justify-center gap-2 py-8 text-xs text-slate-500"
    >
      <InlineSpinner tone="slate" />
      <span>Cargando conversación previa…</span>
    </div>
  );
}

function SuggestionChips({
  onPick,
  disabled,
}: {
  onPick: (prompt: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="assistant-suggestions">
      {SUGGESTED_PROMPTS.map((prompt) => (
        <button
          key={prompt}
          type="button"
          disabled={disabled}
          onClick={() => onPick(prompt)}
          data-testid="assistant-suggestion"
          className="cursor-pointer rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] text-slate-700 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {prompt}
        </button>
      ))}
    </div>
  );
}

function TurnBubble({
  turn,
  projectId,
  municipality,
  onRated,
}: {
  turn: ChatTurn;
  projectId: string;
  municipality: string;
  onRated: (rating: 1 | -1) => void;
}) {
  const isUser = turn.role === "user";
  return (
    <li
      data-testid={isUser ? "assistant-turn-user" : "assistant-turn-assistant"}
      className={isUser ? "flex justify-end" : "flex justify-start"}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
          isUser ? "bg-brand-700 text-white" : "border border-slate-200 bg-white text-slate-900"
        }`}
      >
        {/*
          Sprint 18.6.2: respuestas del assistant pueden traer markdown
          (negrita, listas) — antes se veían los asteriscos literales.
          El bubble del usuario se queda en texto plano (lo que escribió
          se respeta letra por letra, sin re-parsear) para evitar que
          un `**hola**` accidental aparezca como `<strong>`.
        */}
        {isUser ? (
          <p className="whitespace-pre-wrap">{turn.content || "—"}</p>
        ) : (
          <MarkdownLite source={turn.content || "—"} />
        )}

        {!isUser && turn.truncated && (
          <p
            data-testid="assistant-truncated"
            className="mt-2 rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-800"
          >
            Respuesta incompleta — el asistente entró en demasiadas rondas. Intenta una pregunta más
            específica.
          </p>
        )}

        {!isUser && turn.toolCalls && turn.toolCalls.length > 0 && (
          <ToolCallsBadge calls={turn.toolCalls} />
        )}

        {!isUser && turn.auditId && (
          <RatingControls
            projectId={projectId}
            municipality={municipality}
            auditId={turn.auditId}
            currentRating={turn.rating ?? null}
            onRated={onRated}
          />
        )}
      </div>
    </li>
  );
}

/**
 * Mapa de nombre técnico de tool → etiqueta humana en español.
 *
 * **Sprint 18.6.2 (mayo 20, 2026)**: antes mostrábamos al usuario el
 * nombre Python crudo (`get_next_legal_transitions`). El funcionario no
 * lo entiende y tampoco le aporta — solo le importa SI el asistente
 * consultó algo y CUÁNTO. Acá lo traducimos a etiquetas legibles.
 *
 * Si una tool nueva aparece sin entrada en este mapa, caemos a una
 * etiqueta genérica ("Consulta interna") en vez del nombre técnico.
 * Eso evita filtrar internals si alguien suma una tool y se olvida de
 * mapearla.
 */
const TOOL_FRIENDLY_LABELS: Record<string, string> = {
  get_project_context: "Estado del expediente",
  get_pending_required_items: "Items pendientes del checklist",
  get_next_legal_transitions: "Próximos estados posibles",
  get_documents_summary: "Documentos adjuntos",
  get_municipality_context: "Perfil del municipio",
  get_similar_past_projects: "Proyectos similares aprobados",
  get_subsanation_examples: "Ejemplos de subsanación",
};

function friendlyToolLabel(name: string): string {
  return TOOL_FRIENDLY_LABELS[name] ?? "Consulta interna";
}

function ToolCallsBadge({ calls }: { calls: AssistantToolCallDTO[] }) {
  const errorCount = calls.filter((c) => c.is_error).length;
  return (
    <details
      data-testid="assistant-tools-badge"
      className="mt-2 border-t border-slate-200 pt-2 text-[11px] text-slate-500"
    >
      <summary className="cursor-pointer font-semibold select-none">
        {calls.length === 1
          ? "Consultó 1 fuente del expediente"
          : `Consultó ${calls.length} fuentes del expediente`}
        {errorCount > 0 && <span className="ml-1 text-red-600">({errorCount} con error)</span>}
      </summary>
      <ul className="mt-1 space-y-0.5">
        {calls.map((call, idx) => (
          <li
            key={idx}
            data-testid="assistant-tool-call-item"
            className={call.is_error ? "text-red-600" : "text-slate-600"}
          >
            <span className="font-medium">{friendlyToolLabel(call.name)}</span>
            {call.is_error && <span className="ml-1">⚠</span>}
          </li>
        ))}
      </ul>
    </details>
  );
}

function RatingControls({
  projectId,
  municipality,
  auditId,
  currentRating,
  onRated,
}: {
  projectId: string;
  municipality: string;
  auditId: string;
  currentRating: 1 | -1 | null;
  onRated: (rating: 1 | -1) => void;
}) {
  const rateMutation = useMutation<void, Error, 1 | -1>({
    mutationFn: (rating) =>
      rateExpedienteAssistantResponse({
        projectId,
        auditId,
        municipality,
        rating,
      }),
    onSuccess: (_, rating) => {
      onRated(rating);
      // Sprint 17.5 telemetría: cada rating dispara un evento.
      // El backend ya persiste el rating en el audit log, pero el
      // evento aquí permite cruzar con DAU y otros para medir tasa
      // de rateado.
      track({
        event_name: "assistant_rated",
        properties: {
          project_id: projectId,
          dane: municipality,
          audit_id: auditId,
          rating,
        },
      });
    },
  });

  return (
    <div
      data-testid="assistant-rating"
      className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-500"
    >
      <span>¿Te sirvió?</span>
      <button
        type="button"
        disabled={rateMutation.isPending || currentRating !== null}
        onClick={() => rateMutation.mutate(1)}
        data-testid="assistant-rate-up"
        aria-label="Útil"
        className={`cursor-pointer rounded px-1.5 py-0.5 transition-colors hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 ${
          currentRating === 1 ? "bg-emerald-100 text-emerald-700" : ""
        }`}
      >
        👍
      </button>
      <button
        type="button"
        disabled={rateMutation.isPending || currentRating !== null}
        onClick={() => rateMutation.mutate(-1)}
        data-testid="assistant-rate-down"
        aria-label="No útil"
        className={`cursor-pointer rounded px-1.5 py-0.5 transition-colors hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50 ${
          currentRating === -1 ? "bg-red-100 text-red-700" : ""
        }`}
      >
        👎
      </button>
      {currentRating !== null && (
        <span className="text-emerald-700">¡Gracias por el feedback!</span>
      )}
    </div>
  );
}

function PendingBubble() {
  return (
    <li className="flex justify-start" data-testid="assistant-pending">
      <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-600">
        <InlineSpinner tone="slate" />
        <span>Consultando datos del expediente…</span>
      </div>
    </li>
  );
}

function LockedPanel({ reason, upgradeTo }: { reason: string; upgradeTo: string | null }) {
  const isTierLock = reason === "tier_locked_pro_required" || upgradeTo === "pro";
  const isQuotaExhausted = reason === "ai_quota_exhausted";

  return (
    <section
      aria-label="Asistente IA bloqueado"
      data-testid="expediente-assistant-locked"
      className="rounded-2xl border border-slate-200 bg-slate-50 p-6 shadow-xs"
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 rounded-full bg-brand-100 p-2 text-brand-700">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-slate-900">
            {isTierLock ? "Asistente IA — disponible en plan Pro" : null}
            {isQuotaExhausted ? "Cuota de mensajes IA agotada este mes" : null}
            {!isTierLock && !isQuotaExhausted ? "Asistente IA no disponible" : null}
          </h2>
          <p className="mt-1 text-xs text-slate-600">
            {isTierLock && (
              <>
                El plan Esencial no incluye el asistente IA. Sube a Pro para pedirle al asistente
                que te oriente sobre qué te falta, qué proyectos similares aplican, y cómo armar la
                subsanación.
              </>
            )}
            {isQuotaExhausted && (
              <>
                Tu muni ya usó los 150 mensajes IA incluidos este mes. La cuota se renueva el primer
                día del próximo mes. Si necesitas más, escríbenos.
              </>
            )}
            {!isTierLock && !isQuotaExhausted && (
              <>El asistente no está disponible en este momento. Intenta nuevamente más tarde.</>
            )}
          </p>
          {isTierLock && (
            <a
              href="mailto:hola@kaptar.com.co?subject=Quiero%20subir%20a%20Pro"
              data-testid="assistant-upgrade-cta"
              className="mt-3 inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white shadow-xs transition-colors hover:bg-brand-800"
            >
              Pedir upgrade a Pro
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
